import React, { useEffect, useRef, useState } from 'react';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

async function apiFetch(path, options = {}) {
  const token = localStorage.getItem('accessToken');
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.detail || payload.error || `Request failed (${response.status})`);
  return payload;
}

const ACTIVE_STATUSES = new Set(['queued', 'in_progress']);

export default function InstallmentPaymentFilingPanel({ caseId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [preflight, setPreflight] = useState(null);
  const [filing, setFiling] = useState(null);
  const [amount, setAmount] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [error, setError] = useState('');
  const notifiedFiling = useRef('');

  const loadPreflight = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch(`/firm/cases/${caseId}/cmecf-installment-payment/preflight/`);
      setPreflight(data);
      setAmount(data.suggested_amount || '');
      const latest = data.latest_filing;
      setFiling(
        latest && (
          ['queued', 'in_progress', 'review_required'].includes(latest.status)
          || (latest.status === 'succeeded' && !latest.reconciled)
        )
          ? latest
          : null
      );
      setAcknowledged(false);
      setConfirmationText('');
    } catch (requestError) {
      setError(requestError.message || 'Could not review the installment payment.');
    } finally {
      setLoading(false);
    }
  };

  const openReview = () => {
    setOpen(true);
    loadPreflight();
  };

  useEffect(() => {
    if (!open || !filing?.id || !ACTIVE_STATUSES.has(filing.status)) return undefined;
    let cancelled = false;
    let polling = false;
    const poll = async () => {
      if (cancelled || polling) return;
      polling = true;
      try {
        const current = await apiFetch(`/firm/cmecf-installment-payments/${filing.id}/`);
        if (cancelled) return;
        setFiling(current);
        if (!ACTIVE_STATUSES.has(current.status) && notifiedFiling.current !== current.id) {
          notifiedFiling.current = current.id;
          onChanged?.();
        }
      } catch (requestError) {
        if (!cancelled) setError(requestError.message || 'Could not check the filing status.');
      } finally {
        polling = false;
      }
    };
    poll();
    const timer = window.setInterval(poll, 2000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [caseId, filing?.id, filing?.status, onChanged, open]);

  const startFiling = async () => {
    setLoading(true);
    setError('');
    try {
      const current = await apiFetch(
        `/firm/cases/${caseId}/cmecf-installment-payment/start/`,
        {
          method: 'POST',
          body: JSON.stringify({
            amount,
            filing_acknowledged: acknowledged,
            confirmation_text: confirmationText,
          }),
        }
      );
      setFiling(current);
    } catch (requestError) {
      setError(requestError.message || 'The installment payment could not be started.');
    } finally {
      setLoading(false);
    }
  };

  const cancelQueued = async () => {
    if (!window.confirm('Cancel this request? Nothing can be cancelled after the worker begins filing.')) return;
    setLoading(true);
    setError('');
    try {
      const current = await apiFetch(
        `/firm/cmecf-installment-payments/${filing.id}/cancel-queued/`,
        { method: 'POST', body: JSON.stringify({}) }
      );
      setFiling(current);
      await loadPreflight();
    } catch (requestError) {
      setError(requestError.message || 'The queued filing could not be cancelled.');
    } finally {
      setLoading(false);
    }
  };

  const resolveUncertain = async (outcome) => {
    const prompt = outcome === 'filed'
      ? 'Confirm only after checking the live docket and finding this installment payment. Mark it filed?'
      : 'Confirm only after checking the live docket and verifying no installment payment was filed. Allow another attempt?';
    if (!window.confirm(prompt)) return;
    setLoading(true);
    setError('');
    try {
      const current = await apiFetch(
        `/firm/cmecf-installment-payments/${filing.id}/resolve/`,
        { method: 'POST', body: JSON.stringify({ outcome }) }
      );
      setFiling(current);
      await loadPreflight();
      onChanged?.();
    } catch (requestError) {
      setError(requestError.message || 'The docket review could not be saved.');
    } finally {
      setLoading(false);
    }
  };

  const amountNumber = Number(amount);
  const availableNumber = Number(preflight?.available_amount || 0);
  const canStart = Boolean(
    preflight?.ready
    && amountNumber > 0
    && amountNumber <= availableNumber
    && acknowledged
    && confirmationText.trim().toUpperCase() === 'FILE'
    && !filing
  );

  return (
    <>
      <button
        type="button"
        onClick={openReview}
        className="mt-2 inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
      >
        <LockClosedIcon className="h-4 w-4" />
        File installment payment
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/55 p-4">
          <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-slate-200 p-5">
              <div>
                <h2 className="text-xl font-bold text-slate-900">File Chapter 7 installment payment</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Review the payment amount before creating a live CM/ECF docket entry.
                </p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="rounded p-1 text-slate-500 hover:bg-slate-100">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-5 p-5">
              {loading && !preflight && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <ArrowPathIcon className="h-5 w-5 animate-spin" /> Reviewing case totals…
                </div>
              )}

              {preflight && (
                <div className="grid grid-cols-2 gap-3 rounded-lg bg-slate-50 p-4 text-sm sm:grid-cols-5">
                  <div><span className="block text-slate-500">Case</span><strong>{preflight.case_number || '—'}</strong></div>
                  <div><span className="block text-slate-500">Paid to firm</span><strong>${preflight.filing_fees_paid}</strong></div>
                  <div><span className="block text-slate-500">Court receipted</span><strong>${preflight.filing_fees_receipted}</strong></div>
                  <div><span className="block text-slate-500">Pending/recently filed</span><strong>${preflight.reserved_amount}</strong></div>
                  <div><span className="block text-slate-500">Available now</span><strong>${preflight.available_amount}</strong></div>
                </div>
              )}

              {preflight && !preflight.ready && !filing && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
                  <div className="flex items-start gap-2">
                    <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" />
                    <div>
                      <strong>This payment is not ready to file.</strong>
                      <ul className="mt-2 list-disc space-y-1 pl-5">
                        {(preflight.reasons || []).map(reason => <li key={reason}>{reason}</li>)}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {!filing && preflight?.ready && (
                <>
                  <label className="block text-sm font-medium text-slate-800">
                    Amount to receipt with the court
                    <div className="mt-1 flex items-center rounded-md border border-slate-300 bg-white focus-within:ring-2 focus-within:ring-indigo-500">
                      <span className="pl-3 text-slate-500">$</span>
                      <input
                        type="number"
                        min="0.01"
                        max={preflight.available_amount}
                        step="0.01"
                        value={amount}
                        onChange={event => setAmount(event.target.value)}
                        className="w-full rounded-md border-0 px-2 py-2 focus:ring-0"
                      />
                    </div>
                    <span className="mt-1 block text-xs font-normal text-slate-500">
                      Defaults to paid (${preflight.filing_fees_paid}) less court-receipted (${preflight.filing_fees_receipted}) and pending/recently filed (${preflight.reserved_amount}). You may lower it, but not exceed ${preflight.available_amount}.
                    </span>
                  </label>

                  <div className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-900">
                    <strong>Live court filing</strong>
                    <p className="mt-1">This files an Installment Payment event. No PDF is attached to this event.</p>
                  </div>

                  <label className="flex items-start gap-3 text-sm text-slate-800">
                    <input type="checkbox" checked={acknowledged} onChange={event => setAcknowledged(event.target.checked)} className="mt-1 rounded border-slate-300" />
                    <span>I verified the amount actually paid by the debtor and authorize this live court filing.</span>
                  </label>

                  <label className="block text-sm font-medium text-slate-800">
                    Type FILE to confirm
                    <input
                      value={confirmationText}
                      onChange={event => setConfirmationText(event.target.value)}
                      className="mt-1 w-full rounded-md border-slate-300"
                      autoComplete="off"
                    />
                  </label>

                  <button
                    type="button"
                    disabled={!canStart || loading}
                    onClick={startFiling}
                    className="inline-flex items-center gap-2 rounded-md bg-red-600 px-4 py-2 font-semibold text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {loading ? <ArrowPathIcon className="h-5 w-5 animate-spin" /> : <LockClosedIcon className="h-5 w-5" />}
                    File ${Number(amount || 0).toFixed(2)} installment payment
                  </button>
                </>
              )}

              {filing && (
                <div className={`rounded-lg border p-4 ${filing.status === 'succeeded' ? 'border-green-300 bg-green-50' : filing.status === 'review_required' ? 'border-red-300 bg-red-50' : filing.status === 'failed' ? 'border-amber-300 bg-amber-50' : 'border-indigo-200 bg-indigo-50'}`}>
                  <div className="flex items-start gap-3">
                    {filing.status === 'succeeded'
                      ? <CheckCircleIcon className="h-6 w-6 shrink-0 text-green-700" />
                      : filing.status === 'failed' || filing.status === 'review_required'
                        ? <ExclamationTriangleIcon className="h-6 w-6 shrink-0 text-amber-700" />
                        : <ArrowPathIcon className="h-6 w-6 shrink-0 animate-spin text-indigo-700" />}
                    <div className="min-w-0 text-sm">
                      <strong>{filing.status_display}: ${filing.amount}</strong>
                      <p className="mt-1">{filing.status_message}</p>
                      {filing.docket_number && <p className="mt-1">Docket number: {filing.docket_number}</p>}
                      {filing.status === 'succeeded' && !filing.reconciled && (
                        <p className="mt-2 text-slate-600">The amount is reserved now. The case’s receipted total will update from the court docket notice.</p>
                      )}
                      {filing.error_message && <p className="mt-2 whitespace-pre-wrap text-red-800">{filing.error_message}</p>}
                    </div>
                  </div>

                  {filing.status === 'queued' && filing.can_cancel_queued && (
                    <button type="button" onClick={cancelQueued} disabled={loading} className="mt-3 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                      Cancel stalled request
                    </button>
                  )}
                  {filing.status === 'review_required' && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      <button type="button" onClick={() => resolveUncertain('filed')} disabled={loading} className="rounded-md bg-green-700 px-3 py-2 text-sm font-semibold text-white hover:bg-green-800">It is on the docket</button>
                      <button type="button" onClick={() => resolveUncertain('not_filed')} disabled={loading} className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">It was not filed</button>
                    </div>
                  )}
                  {['succeeded', 'failed'].includes(filing.status) && (
                    <button type="button" onClick={loadPreflight} className="mt-3 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Refresh payment review</button>
                  )}
                </div>
              )}

              {error && <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
